import React, { Fragment, useImperativeHandle, useRef, useState } from "react";
import { toast } from "react-toastify";
import commonNetwork from "../../common/api/commonNetwork";
import { Button } from "./Elements";
import { Viewer, Worker } from '@react-pdf-viewer/core';
import Loading from "./Loading";
import { Dialog, Transition } from "@headlessui/react";
import '@react-pdf-viewer/core/lib/styles/index.css';

type Props = {
    isLoading: boolean,
    setIsLoading: (isLoading: boolean) => void
}
const PaymentSlipModal = React.forwardRef(({ isLoading, setIsLoading }: Props, ref) => {

    const [isVisible, setIsVisible] = useState(false);
    const [slipUrl, setSlipUrl] = useState("");
    const [downloadSlipUrl, setDownloadSlipUrl] = useState("");
    const [storedOrderId, setStoredOrderId] = useState(0)

    useImperativeHandle(ref, () => ({

        showPaymentSlip({ orderId }) {

            selectPaymentSlipUrl(orderId);
        },
        downloadPaymentSlip({ orderId, orderCode }) {

            downloadPaymentSlip(orderId, orderCode);
        },
    }));

    const isMobile = () => {
        return /Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(
            navigator.userAgent
        );
    };

    const createBlobUrl = (blob) => {
        return window.URL.createObjectURL(blob);
    };

    const selectPaymentSlipUrl = async (orderId) => {

        try {
            const urlParams = new URLSearchParams(window.location.search);
            const hash = urlParams.get('hash');
            setStoredOrderId(orderId);

            setIsLoading(true);

            const params = { orderId };

            const { isSuccess, data, errorMessage } =
                await commonNetwork.get("/payment/selectPaymentSlipUrl", params);

            if (!isSuccess) {
                toast.error(errorMessage);
                setIsLoading(false);
                return;
            }

            const { innerhtmlurl } = data;

            let apiPath = innerhtmlurl;

            if (hash) {
                apiPath = apiPath + "&hash=" + hash;
            }

            const result = await commonNetwork.downloadFileGetRequest(apiPath);

            if (!result.isSuccess) {
                toast.error("Dekont yüklenemedi");
                setIsLoading(false);
                return;
            }

            const blob = result.data;
            const blobUrl = createBlobUrl(blob);

            if (isMobile()) {
                // MOBİL -> modal
                setSlipUrl(blobUrl);
                setIsVisible(true);
            } else {
                // DESKTOP → iframe
                const w = 826;
                const h = 1066;

                const win = window.open(
                    "about:blank",
                    "popUpWindow",
                    `width=${w},height=${h},resizable=yes,scrollbars=yes`
                );

                win.document.open();
                win.document.write(`
                                <html>
                                    <body style="margin:0">
                                        <iframe
                                            src="${blobUrl}"
                                            width="100%"
                                            height="100%"
                                            frameborder="0">
                                        </iframe>
                                    </body>
                                </html>
                                `);
                win.document.close();
            }
        } catch (error) {
            toast.error("Dekont yüklenemedi");
        }
        finally {
            setIsLoading(false);
        }
    };

    const downloadPaymentSlip = async (orderId, orderCode) => {

        try {
            setIsLoading(true);

            const params = { orderId };

            const { isSuccess, data, errorMessage } =
                await commonNetwork.get("/payment/selectPaymentSlipUrl", params);

            if (!isSuccess) {
                setIsLoading(false);
                return;
            }

            const { downloadurl } = data;

            const result = await commonNetwork.downloadFileGetRequest(
                downloadurl,
                null
            );

            if (!result.isSuccess) {
                setIsLoading(false);
                return;
            }

            const blob = result.data;

            const fileURL = window.URL.createObjectURL(blob);
            const fileName = `${orderCode}_Ödeme_Dekontu.pdf`;

            const alink = document.createElement("a");
            alink.href = fileURL;
            alink.download = fileName;
            alink.click();

            window.URL.revokeObjectURL(fileURL);
        }
        catch (error) {
            toast.error("Dekont indirilemedi");
        }
        finally {
            setIsLoading(false);
        }
    };

    const onDownloadClick = async () => {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const hash = urlParams.get('hash');
            setIsLoading(true);

            const params = { orderId: storedOrderId };

            const { isSuccess, data, errorMessage } = await commonNetwork.get("/payment/selectPaymentSlipUrl", params);

            if (!isSuccess) {
                setIsLoading(false);
                return;
            }

            let { downloadurl } = data;

            if (hash) {
                downloadurl = downloadurl + "&hash=" + hash;
            }

            const urlObj = new URL(downloadurl);
            const orderCode = urlObj.searchParams.get("OrderCode") || "Dekont";

            const result = await commonNetwork.downloadFileGetRequest(
                downloadurl,
                null
            );

            if (!result.isSuccess) {
                setIsLoading(false);
                return;
            }

            const blob = result.data;

            const fileURL = window.URL.createObjectURL(blob);
            const fileName = `${orderCode}_Ödeme_Dekontu.pdf`;

            const alink = document.createElement("a");
            alink.href = fileURL;
            alink.download = fileName;
            alink.click();

            window.URL.revokeObjectURL(fileURL);
        }
        catch (error) {
            toast.error("Dekont indirilemedi");
        }
        finally {
            setIsLoading(false);
        }
    }

    return (
        <Modal openModal={isVisible} setOpenModal={setIsVisible}>
            <div className="flex items-center justify-center min-h-screen p-4">
                <div className="relative bg-white rounded-lg shadow-xl dark:bg-gray-700 w-full max-w-4xl overflow-hidden flex flex-col"
                    style={{ height: '90vh' }}>

                    <div className="flex justify-center items-center p-4 space-x-3 border-b shrink-0">
                        <Button
                            color="gray"
                            hover="gray"
                            size="xs"
                            callback={() => setIsVisible(false)}
                        >
                            {"Kapat"}
                        </Button>

                        <Button
                            color="primary"
                            hover="primary"
                            size="xs"
                            callback={() => onDownloadClick()}
                        >
                            {"İndir"}
                        </Button>
                    </div>

                    {/* Body: PDF Viewer Alanı */}
                    <div className="flex-1 overflow-hidden relative bg-gray-100">
                        {isLoading ? (
                            <div className="flex items-center justify-center h-full">
                                <Loading />
                            </div>
                        ) : (
                            <div className="h-full w-full overflow-auto">
                                <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                                    <Viewer
                                        fileUrl={slipUrl}
                                        defaultScale={0.8}
                                        renderTextLayer={false}
                                    />
                                </Worker>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
})
export default PaymentSlipModal;

function Modal({ openModal, setOpenModal, children, containerclass }) {

    const cancelButtonRef = useRef(null);

    return (
        <Transition.Root show={openModal || false} as={Fragment}>
            <Dialog
                as="div"
                className="relative z-50"
                initialFocus={cancelButtonRef}
                onClose={setOpenModal}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>
                <div className={"fixed z-10 inset-0 overflow-y-auto "}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <Dialog.Panel className="">
                            {children}
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
